import React, { useEffect, useState } from 'react';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Button, Grid2, TextField, Snackbar, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, MenuItem, Select, InputLabel } from '@mui/material';
import instance from '../axios/axios';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmDialog from './ConfirmDialog';

function DashboardManagement() {
    const [addDashboard, setAddDashboard] = useState(false);
    const [dashboardName, setDashboardName] = useState('');
    const [dashboardPath, setDashboardPath] = useState('');
    const [chartType, setChartType] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [requiredFields, setRequiredFields] = useState([]);
    const [allDashboards, setAllDashboards] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('info');
    const [openAddDashboard, setOpenAddDashboard] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [dashboardToDelete, setDashboardToDelete] = useState(null);
    const [editDashboardDialogOpen, setEditDashboardDialogOpen] = useState(false);
    const [selectedDashboard, setSelectedDashboard] = useState(null);
    const [editDashboardName, setEditDashboardName] = useState('');
    const [editDashboardPath, setEditDashboardPath] = useState('');
    const [editChartType, setEditChartType] = useState('');
    const [editApiKey, setEditApiKey] = useState('');
    const [editRequiredFields, setEditRequiredFields] = useState([]);
    const [editSubmitDisabled, setEditSubmitDisabled] = useState(true);

    const handleDashboardClear = () => {
        setDashboardName('');
        setDashboardPath('');
        setChartType('');
        setApiKey('');
        setRequiredFields([]);
        setOpenAddDashboard(false);
    };

    const handleClick = () => {
        setOpenAddDashboard(!openAddDashboard);
    };

    useEffect(() => {
        const fetchDashboards = async () => {
            try {
                const response = await instance.post('/reports/dashboards/all', {}, { withCredentials: true });
                if (response.status === 200) {
                    setAllDashboards(response.data.data.dashboards);
                } else {
                    handleSnackbarOpen('Error fetching dashboards', 'error');
                }
            } catch (error) {
                console.error('Fetch error:', error);
                handleSnackbarOpen('Error fetching dashboards', 'error');
            }
            if (refresh) {
                setRefresh(false);
            }
        };
        fetchDashboards();
    }, [addDashboard, refresh]);

    // Edit validation
    useEffect(() => {
        const checkEditSubmitDisabled = () => {
            const isNameValid = editDashboardName && checkNameUniqueness(editDashboardName, selectedDashboard?.id);
            const isPathValid = editDashboardPath && validateUrl(editDashboardPath);
            const isChartTypeValid = editChartType && validateChartType(editChartType);
            
            if (isNameValid && isPathValid && isChartTypeValid) {
                setEditSubmitDisabled(false);
            } else {
                setEditSubmitDisabled(true);
            }
        };
        checkEditSubmitDisabled();
    }, [editDashboardName, editDashboardPath, editChartType, selectedDashboard, allDashboards]);

    const createPayload = () => {
        let pl;
        if (requiredFields.length === 0) {
            pl = {
                name: dashboardName,
                path: dashboardPath,
                api_key: apiKey,
                chart_type: chartType,
            };
        } else {
            const filteredParams = requiredFields.filter(param => param.field_name !== '' && param.field_type !== '');
            pl = {
                name: dashboardName,
                path: dashboardPath,
                required_params: filteredParams,
                api_key: apiKey,
                chart_type: chartType
            };
        }
        if (apiKey === '') {
            delete pl.api_key;
        }
        return pl;
    };

    const handleSnackbarOpen = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleDeleteDashboard = async (index) => {
        const response = await instance.post(`/reports/dashboards/${allDashboards[index].id}/delete`, {}, { withCredentials: true });
        if (response.status === 200) {
            const updatedDashboards = allDashboards.filter((dashboard, i) => i !== index);
            setAllDashboards(updatedDashboards);
            handleSnackbarOpen('Dashboard deleted successfully', 'success');
        } else {
            handleSnackbarOpen('Error deleting dashboard', 'error');
        }
    };

    const handleDeleteDashboardClick = (index) => {
        setDashboardToDelete({ index, dashboard: allDashboards[index] });
        setDeleteConfirmOpen(true);
    };

    const handleDeleteDashboardConfirm = () => {
        if (dashboardToDelete) {
            handleDeleteDashboard(dashboardToDelete.index);
            setDeleteConfirmOpen(false);
            setDashboardToDelete(null);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const payload = createPayload();
        try {
            const response = await instance.post('/reports/dashboard/add', payload, { withCredentials: true });
            if (response.status === 200) {
                handleSnackbarOpen('Dashboard added successfully', 'success');
                setOpenAddDashboard(false);
                setDashboardName('');
                setDashboardPath('');
                setApiKey('');
                setRequiredFields([
                    { field_name: 'start_date', field_type: 'date' },
                    { field_name: 'end_date', field_type: 'date' }
                ]);
                setRefresh(true);
            } else {
                handleSnackbarOpen('Error adding dashboard', 'error');
            }
        } catch (error) {
            console.error('Submission error:', error);
            handleSnackbarOpen('Error adding dashboard', 'error');
        }
    };

    const handleRequiredFieldChange = (index, field, value) => {
        const newRequiredFields = [...requiredFields];
        newRequiredFields[index][field] = value;
        setRequiredFields(newRequiredFields);
    };

    const addRequiredField = () => {
        setRequiredFields([...requiredFields, { field_name: '', field_type: '' }]);
    };

    const removeRequiredField = (index) => {
        const newRequiredFields = requiredFields.filter((_, i) => i !== index);
        setRequiredFields(newRequiredFields);
    };

    // Edit Dashboard Functions
    const handleEditDashboard = (dashboard) => {
        setSelectedDashboard(dashboard);
        setEditDashboardName(dashboard.name);
        setEditDashboardPath(dashboard.path);
        setEditChartType(dashboard.chart_type);
        setEditApiKey(dashboard.api_key || '');
        setEditRequiredFields(dashboard.required_params || []);
        setEditDashboardDialogOpen(true);
    };

    const handleEditDashboardClear = () => {
        setEditDashboardName('');
        setEditDashboardPath('');
        setEditChartType('');
        setEditApiKey('');
        setEditRequiredFields([]);
        setSelectedDashboard(null);
        setEditDashboardDialogOpen(false);
    };

    const validateUrl = (url) => {
        return url.startsWith('http://') || url.startsWith('https://');
    };

    const checkNameUniqueness = (name, currentId) => {
        return !allDashboards.some(dashboard => dashboard.name === name && dashboard.id !== currentId);
    };

    const validateChartType = (chartType) => {
        const validTypes = ['line', 'pie', 'stats'];
        return validTypes.includes(chartType);
    };

    const handleEditRequiredFieldChange = (index, field, value) => {
        const newRequiredFields = [...editRequiredFields];
        newRequiredFields[index][field] = value;
        setEditRequiredFields(newRequiredFields);
    };

    const addEditRequiredField = () => {
        setEditRequiredFields([...editRequiredFields, { field_name: '', field_type: '' }]);
    };

    const removeEditRequiredField = (index) => {
        const newRequiredFields = editRequiredFields.filter((_, i) => i !== index);
        setEditRequiredFields(newRequiredFields);
    };

    const handleEditDashboardSubmit = async () => {
        try {
            const payload = {
                name: editDashboardName,
                path: editDashboardPath,
                chart_type: editChartType,
                required_params: editRequiredFields.filter(param => param.field_name !== '' && param.field_type !== '')
            };

            // Only include api_key if it has a value
            if (editApiKey && editApiKey.trim() !== '') {
                payload.api_key = editApiKey;
            }

            const response = await instance.post(`/reports/dashboards/${selectedDashboard.id}/edit`, payload, { withCredentials: true });
            if (response.status === 200) {
                handleSnackbarOpen('Dashboard updated successfully', 'success');
                setEditDashboardDialogOpen(false);
                setRefresh(true);
            } else {
                handleSnackbarOpen('Failed to update dashboard', 'error');
            }
        } catch (error) {
            console.error('Error updating dashboard:', error);
            handleSnackbarOpen('Error updating dashboard', 'error');
        }
    };

    return (
        <Grid2 container justifyContent="center" alignItems="center">
            <Grid2 item xs={12} md={6} lg={4}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '20px',
                    gap: '20px',
                    width: '100%'
                }}>
                    <h2 style={{
                        fontFamily: "Lato"
                    }}>Dashboard Management</h2>
                    <Button variant="contained" color="primary" onClick={handleClick} style={{ alignSelf: 'flex-end' }}>
                        {openAddDashboard ? 'Close' : 'Add Dashboard'}
                    </Button>
                    <Dialog open={openAddDashboard} onClose={handleDashboardClear}>
                        <DialogTitle>Add a New Dashboard</DialogTitle>
                        <form onSubmit={handleFormSubmit}>
                            <DialogContent>
                                <DialogContentText>
                                    Please fill out the form below to add a new dashboard.
                                </DialogContentText>
                                <TextField
                                    fullWidth
                                    type="text"
                                    label="Dashboard Name"
                                    variant="outlined"
                                    value={dashboardName}
                                    onChange={(e) => setDashboardName(e.target.value)}
                                    helperText="Enter a unique name for the dashboard."
                                    required
                                    margin="dense"
                                />
                                <TextField
                                    fullWidth
                                    type="text"
                                    label="Dashboard Path"
                                    variant="outlined"
                                    value={dashboardPath}
                                    onChange={(e) => setDashboardPath(e.target.value)}
                                    helperText="Enter the path to the dashboard."
                                    required
                                    margin="dense"
                                />
                                <TextField
                                    fullWidth
                                    type="text"
                                    label="API Key"
                                    variant="outlined"
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    helperText="Enter the API key for the dashboard."
                                    required
                                    margin="dense"
                                />
                                <FormControl fullWidth variant="outlined" margin="dense">
                                    <InputLabel id="chart-type-label">Chart Type</InputLabel>
                                    <Select
                                        labelId="chart-type-label"
                                        id="chart-type"
                                        value={chartType}
                                        onChange={(e) => setChartType(e.target.value)}
                                        label="Chart Type"
                                        required
                                    >
                                        <MenuItem value="line">Line Chart</MenuItem>
                                        <MenuItem value="pie">Pie Chart</MenuItem>
                                        <MenuItem value="stats">Stats Box</MenuItem>
                                    </Select>
                                </FormControl>
                                {requiredFields.map((param, index) => (
                                    <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <TextField
                                            fullWidth
                                            type="text"
                                            label={`Parameter ${index + 1} Name`}
                                            variant="outlined"
                                            value={param.field_name}
                                            onChange={(e) => handleRequiredFieldChange(index, 'field_name', e.target.value)}
                                            helperText="Enter the name of the parameter."
                                            margin="dense"
                                        />
                                        <TextField
                                            fullWidth
                                            type="text"
                                            label={`Parameter ${index + 1} Type`}
                                            variant="outlined"
                                            value={param.field_type}
                                            onChange={(e) => handleRequiredFieldChange(index, 'field_type', e.target.value)}
                                            helperText="Enter the type of the parameter."
                                            margin="dense"
                                        />
                                        <Button variant="contained" color="secondary" onClick={() => removeRequiredField(index)}>Remove</Button>
                                    </div>
                                ))}
                                <Button variant="contained" color="primary" onClick={addRequiredField} style={{ marginTop: '10px' }}>Add Required Field</Button>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleDashboardClear} color="primary">
                                    Cancel
                                </Button>
                                <Button type="submit" variant="contained" color="primary">
                                    Add Dashboard
                                </Button>
                            </DialogActions>
                        </form>
                    </Dialog>
                    <div className='dashboard-management'>
                        {allDashboards.length > 0 && addDashboard === false && (
                            <TableContainer>
                                <TableHead>
                                    <TableRow>
                                        <TableCell> </TableCell>
                                        <TableCell>#</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Path</TableCell>
                                        <TableCell> </TableCell>
                                        <TableCell> </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {allDashboards.map((dashboard, index) => (
                                        <TableRow key={index}>
                                            <TableCell><DashboardIcon /></TableCell>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{dashboard.name}</TableCell>
                                            <TableCell>{dashboard.path}</TableCell>
                                            <TableCell>
                                                <Button 
                                                    color="primary" 
                                                    onClick={() => handleEditDashboard(dashboard)}
                                                    startIcon={<EditIcon />}
                                                >
                                                    Edit
                                                </Button>
                                            </TableCell>
                                            <TableCell>
                                                <Button variant="contained" color="secondary" onClick={() => handleDeleteDashboardClick(index)}>
                                                    Delete
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </TableContainer>
                        )}
                    </div>
                </div>
            </Grid2>
            <ConfirmDialog
                open={deleteConfirmOpen}
                onClose={() => {
                    setDeleteConfirmOpen(false);
                    setDashboardToDelete(null);
                }}
                onConfirm={handleDeleteDashboardConfirm}
                title="Delete Dashboard"
                resourceType="Dashboard"
                resourceName={dashboardToDelete?.dashboard?.name}
                requireTypeConfirm={true}
                confirmButtonText="Delete Dashboard"
            />
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <Dialog open={editDashboardDialogOpen} onClose={handleEditDashboardClear} maxWidth="md" fullWidth>
                <DialogTitle>Edit Dashboard</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Update the dashboard information below.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        type="text"
                        label="Dashboard Name"
                        variant="outlined"
                        value={editDashboardName}
                        onChange={(e) => setEditDashboardName(e.target.value)}
                        helperText={!checkNameUniqueness(editDashboardName, selectedDashboard?.id) && editDashboardName ? "Name must be unique" : "Enter a unique name for the dashboard."}
                        required
                        margin="dense"
                        error={!checkNameUniqueness(editDashboardName, selectedDashboard?.id) && editDashboardName}
                    />
                    <TextField
                        fullWidth
                        type="text"
                        label="Dashboard Path"
                        variant="outlined"
                        value={editDashboardPath}
                        onChange={(e) => setEditDashboardPath(e.target.value)}
                        helperText={!validateUrl(editDashboardPath) && editDashboardPath ? "Must start with http:// or https://" : "Enter the path to the dashboard."}
                        required
                        margin="dense"
                        error={!validateUrl(editDashboardPath) && editDashboardPath}
                    />
                    <FormControl fullWidth variant="outlined" margin="dense">
                        <InputLabel id="edit-chart-type-label">Chart Type</InputLabel>
                        <Select
                            labelId="edit-chart-type-label"
                            id="edit-chart-type"
                            value={editChartType}
                            onChange={(e) => setEditChartType(e.target.value)}
                            label="Chart Type"
                            error={!validateChartType(editChartType) && editChartType}
                        >
                            <MenuItem value="line">Line Chart</MenuItem>
                            <MenuItem value="pie">Pie Chart</MenuItem>
                            <MenuItem value="stats">Stats Box</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        type="text"
                        label="API Key"
                        variant="outlined"
                        value={editApiKey}
                        onChange={(e) => setEditApiKey(e.target.value)}
                        helperText="Enter the API key for the dashboard."
                        margin="dense"
                    />
                    {editRequiredFields.map((param, index) => (
                        <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px' }}>
                            <TextField
                                fullWidth
                                type="text"
                                label={`Parameter ${index + 1} Name`}
                                variant="outlined"
                                value={param.field_name}
                                onChange={(e) => handleEditRequiredFieldChange(index, 'field_name', e.target.value)}
                                margin="dense"
                            />
                            <TextField
                                fullWidth
                                type="text"
                                label={`Parameter ${index + 1} Type`}
                                variant="outlined"
                                value={param.field_type}
                                onChange={(e) => handleEditRequiredFieldChange(index, 'field_type', e.target.value)}
                                margin="dense"
                            />
                            <Button variant="contained" color="secondary" onClick={() => removeEditRequiredField(index)}>Remove</Button>
                        </div>
                    ))}
                    <Button variant="contained" color="primary" onClick={addEditRequiredField} style={{ marginTop: '10px' }}>Add Required Field</Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditDashboardClear} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleEditDashboardSubmit} color="primary" disabled={editSubmitDisabled}>
                        Update Dashboard
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid2>
    );
}

export default DashboardManagement;
